import { useEffect, useState } from 'react'
import DetailsTag from './DetailsTag'
import { getSubscriptionByGroupId } from '../../services/Subscription'
import Cookies from 'js-cookie';
import { getPlan } from '../../services/Plans';
import { getMyCdirCreditCardWithTenantIdAndIsActive } from '../../services/BillingCreditCard';
import { getMyCdirACHInfoWithTenantIdAndIsActive } from '../../services/ACHInfo';
import Loading from '../../icons/Loading';
import useGroup from '../../hook/useGroup';
import { formatDateForDisplay } from '../../utils/getUserDateFormat';
import { INVOICE_TYPES, InvoiceType } from './Billing';

type SubscriptionDetailsProps = {
    setShowPopupUpgradeSubscription: (value: boolean) => void;
    planDetails: any;
    setPlanDetails: (value: any) => void;
    paymentType: number,
    setPaymentType: (value: number) => void;
    isProcessing: boolean;
    setIsProcessing: (value: boolean) => void;
    setShowPopupForPayment: (Value: boolean) => void;
    setInvoiceType: (value: InvoiceType) => void;
    isRefreshSubscriptionDetails: boolean;
    setIsRefreshSubscriptionDetails: (value: boolean) => void;
    setActivePaymentDetails: (value: any | null) => void;
    isUpgradingTheSubcription: boolean;
    setIsUpgradingTheSubcription: (value: boolean) => void,
    callTheAPIForUpgradeSubscription: (upgradedPlan: any, paymentDetails: any, cb: any) => void;
}

const SubscriptionDetails = ({
    setShowPopupUpgradeSubscription,
    planDetails,
    setPlanDetails,
    paymentType,
    setPaymentType,
    isProcessing,
    setIsProcessing,
    setShowPopupForPayment,
    setInvoiceType,
    isRefreshSubscriptionDetails,
    setIsRefreshSubscriptionDetails,
    setActivePaymentDetails,
    isUpgradingTheSubcription,
    setIsUpgradingTheSubcription,
    callTheAPIForUpgradeSubscription
}: SubscriptionDetailsProps) => {
    const { selectedTenantId, isSuperAdmin }: any = useGroup();
    const tenantId = isSuperAdmin
        ? selectedTenantId
        : Cookies.get("tenantId-my-cdir");
    const [subscriptionDetails, setSubscriptionDetails] = useState<any>({
        planId: "",
        status: "",
        startDate: "",
        endDate: ""
    });
    const [showPaymentDialog, setShowPaymentDialog] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (tenantId && isRefreshSubscriptionDetails) {
            setIsRefreshSubscriptionDetails(false);
            setLoading(true);
            getSubscriptionByGroupId(tenantId)
                .then(res => {
                    if (res?.planId) {
                        getPlan(res?.planId)
                            .then(res => {
                                setPlanDetails(res);
                            })
                            .catch(error => {
                                console.error(error);
                            });
                    }

                    setSubscriptionDetails({
                        ...res,
                        startDate: res?.startDate ? formatDateForDisplay(res?.startDate) : "",
                        endDate: res?.endDate ? formatDateForDisplay(res?.endDate) : ""
                    });
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, isRefreshSubscriptionDetails])

    const upgradePlan = () => {
        setIsUpgradingTheSubcription(true);
        setShowPaymentDialog(true);
    }

    const renewPlan = () => {
        setIsUpgradingTheSubcription(false);
        setShowPaymentDialog(true);
    }

    const upgradeOrRenewThePlan = () => {
        if (tenantId) {
            if (paymentType === 0) {
                getMyCdirCreditCardWithTenantIdAndIsActive(tenantId)
                    .then(res => {
                        if (isUpgradingTheSubcription) {
                            setActivePaymentDetails(res);
                            setShowPopupUpgradeSubscription(true);
                            setShowPaymentDialog(false);
                        } else {
                            if (isProcessing) return;
                            setIsProcessing(true);

                            callTheAPIForUpgradeSubscription(planDetails, res, () => {
                                setShowPaymentDialog(false);
                            });
                        }
                    })
                    .catch(error => {
                        console.log(error);
                        setActivePaymentDetails(null);
                        setShowPaymentDialog(false);
                        setInvoiceType(INVOICE_TYPES.Subscription);

                        if (isUpgradingTheSubcription) {
                            setShowPopupUpgradeSubscription(true);
                        } else {
                            setShowPopupForPayment(true);
                        }
                    });
            } else {
                getMyCdirACHInfoWithTenantIdAndIsActive(tenantId)
                    .then(res => {
                        if (isUpgradingTheSubcription) {
                            setActivePaymentDetails(res);
                            setShowPopupUpgradeSubscription(true);
                            setShowPaymentDialog(false);
                        } else {
                            if (isProcessing) return;
                            setIsProcessing(true);

                            callTheAPIForUpgradeSubscription(planDetails, res, () => {
                                setShowPaymentDialog(false);
                            });
                        }
                    })
                    .catch(error => {
                        console.log(error);
                        setActivePaymentDetails(null);
                        setShowPaymentDialog(false);
                        setInvoiceType(INVOICE_TYPES.Subscription);

                        if (isUpgradingTheSubcription) {
                            setShowPopupUpgradeSubscription(true);
                        } else {
                            setShowPopupForPayment(true);
                        }
                    });
            }
        } else {
            setShowPopupUpgradeSubscription(true);
        }
    }

    return (
        <>
            <div className="w-full border border-secondary rounded-md bg-primary">
                <h4 className="text-primary font-medium bg-secondary px-4 py-2 w-full">Subscription Details</h4>
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#2B3998]" />
                        <span className="text-sm text-gray-500">
                            Loading...
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col p-4 gap-y-3">

                        <div className="flex gap-x-4">
                            <DetailsTag
                                title="Billing Plan"
                                value={
                                    `${planDetails?.planName} $${planDetails?.price ? planDetails?.price?.toFixed(2) : "0.00"}/${planDetails?.billingCycle}`
                                    + `${subscriptionDetails?.isTrial ? ' - Free Trial' : ''}`
                                }
                            />

                            <div className="w-1/6">
                                <DetailsTag
                                    title="Status"
                                    value={subscriptionDetails?.status}
                                    bg={"#4ade80"}
                                />

                            </div>
                        </div>

                        <div className="flex gap-x-4">
                            <DetailsTag
                                title="Start Date"
                                value={subscriptionDetails?.startDate}
                            />


                            <DetailsTag
                                title="Next Invoice Date"
                                value={subscriptionDetails?.endDate}
                            />
                        </div>

                        <div className="flex gap-x-4">
                            <button
                                className="bg-secondary rounded-md px-4 py-1.5 font-medium text-white"
                                onClick={renewPlan}
                            >
                                Renew
                            </button>

                            <button
                                className="bg-green-600 rounded-md px-4 py-1.5 font-medium text-white"
                                onClick={upgradePlan}
                            >
                                Upgrade
                            </button>
                        </div>

                    </div>
                )}
            </div>

            {
                showPaymentDialog && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4">

                        <div className="w-full max-w-[400px] rounded-xl bg-white p-6 shadow-2xl animate-fadeIn">

                            <h3 className="text-lg font-semibold mb-5 text-[#181842]">
                                Select Payment Method
                            </h3>

                            <div className="flex flex-col gap-4">

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="payment"
                                        className="accent-[#167AC3] w-4 h-4"
                                        checked={paymentType === 0}
                                        onChange={() => setPaymentType(0)}
                                    />

                                    <span className="text-sm text-gray-700">
                                        Credit Card
                                    </span>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="payment"
                                        className="accent-[#167AC3] w-4 h-4"
                                        checked={paymentType === 1}
                                        onChange={() => setPaymentType(1)}
                                    />

                                    <span className="text-sm text-gray-700">
                                        Bank ACH
                                    </span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 mt-8">

                                <button
                                    type="button"
                                    onClick={upgradeOrRenewThePlan}
                                    className="min-w-[100px] rounded-md bg-[#167AC3] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#1267a7]"
                                >
                                    {
                                        isProcessing
                                            ? <Loading />
                                            : "Continue"
                                    }
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowPaymentDialog(false)}
                                    className="min-w-[100px] rounded-md border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    )
}

export default SubscriptionDetails
